import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import { ApprovalTableUnit } from '@/components/Approval/@components/ApprovalFormTable';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import useLayoutSize from '@/hooks/useLayoutSize';
import {
  formatStoreOpenThousand,
  type StoreOpenInteriorDetail,
  type StoreOpenInteriorLine,
} from '@/utils/approval/storeOpen';
import {
  StoreOpenDetailMessage,
  StoreOpenDetailRoot,
  StoreOpenSheetBody,
} from './styles';

const CATEGORY_TOTAL_LABELS: [string, string][] = [
  ['A', '비매뉴얼 Total'],
  ['B', '매뉴얼 Total'],
  ['C', '집기공사 Total'],
];

/** 같은 값이 이어지는 묶음의 첫 줄에만 칸을 그리고 rowSpan 으로 합침 */
const spanAt = (
  lines: StoreOpenInteriorLine[],
  index: number,
  keyOf: (line: StoreOpenInteriorLine) => string,
): number => {
  const key = keyOf(lines[index]);
  if (index > 0 && keyOf(lines[index - 1]) === key) return 0;
  let span = 1;
  while (index + span < lines.length && keyOf(lines[index + span]) === key) {
    span += 1;
  }
  return span;
};

const cat1 = (l: StoreOpenInteriorLine) => l.category1Code ?? '';
const cat2 = (l: StoreOpenInteriorLine) =>
  `${cat1(l)}|${l.category2Code ?? ''}`;
const cat3 = (l: StoreOpenInteriorLine) =>
  `${cat2(l)}|${l.category3Code ?? ''}`;

const thousand = (value: string | null) => {
  const n = Number(value ?? '');
  return Number.isFinite(n) ? n / 1000 : 0;
};

const DROP_TAGS = new Set(['style', 'script', 'link', 'input', 'button']);
// 표 태그 사이 공백 글자는 React 경고(hydration)
const TABLE_PARTS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'colgroup',
]);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 레거시 문서 — 매장을 고를 때 저장해 둔 팝업 HTML 그대로 */
const LegacySnapshot = ({ html }: { html: string }) => {
  const transform = useCallback<ElementTransform>(({ tagName, children }) => {
    if (DROP_TAGS.has(tagName)) return null;
    if (TABLE_PARTS.has(tagName)) {
      const kept = children.filter((c) => !isBlankText(c));
      children.splice(0, children.length, ...kept);
    }
    return undefined;
  }, []);
  const rendered = useHtmlToReact(html, { transform });
  return <StoreOpenDetailRoot>{rendered}</StoreOpenDetailRoot>;
};

type BodyProps = {
  detail: StoreOpenInteriorDetail | null;
  /** 레거시 문서의 저장 시점 HTML — 있으면 detail 대신 그대로 보여줌 */
  legacyHtml?: string | null;
  /** TOTAL(기업이윤 포함) — 천 원, 저장값 또는 작성 시 예상비용 */
  total: string;
  isLoading: boolean;
  isError: boolean;
  t: TFunction;
};

/** 레거시 appr_sub_view_forms_open_depart_detail_view */
const InteriorDetailBody = ({
  detail,
  legacyHtml,
  total,
  isLoading,
  isError,
  t,
}: BodyProps) => {
  if (isLoading)
    return (
      <StoreOpenDetailMessage>
        {t('데이터를 불러오는 중입니다.')}
      </StoreOpenDetailMessage>
    );
  if (legacyHtml) return <LegacySnapshot html={legacyHtml} />;
  if (isError || !detail)
    return (
      <StoreOpenDetailMessage>
        {t('데이터를 불러오지 못했습니다.')}
      </StoreOpenDetailMessage>
    );

  const { lines } = detail;
  const categoryAmount = (code: string) =>
    detail.categoryAmounts.find((c) => c.categoryCode === code)?.amount ?? null;

  return (
    <StoreOpenDetailRoot>
      <ApprovalTableUnit>{t('(단위 : 천)')}</ApprovalTableUnit>
      <table className="store-open-fit">
        <tbody>
          <tr>
            <th>{detail.shopName ?? ''}</th>
          </tr>
        </tbody>
      </table>
      <table className="store-open-fit">
        <colgroup>
          <col style={{ width: '30%' }} />
          <col style={{ width: '70%' }} />
        </colgroup>
        <tbody>
          <tr>
            <th colSpan={2}>{t('요약')}</th>
          </tr>
          <tr>
            <td>{t('TOTAL(기업이윤 포함)')}</td>
            <td className="align-right">{total}</td>
          </tr>
          <tr>
            <td>{t('기업이윤 %')}</td>
            <td className="align-right">
              {detail.rawTotalProfitApplyRate ?? ''}
            </td>
          </tr>
          {CATEGORY_TOTAL_LABELS.map(([code, label]) => (
            <tr key={code}>
              <td>{t(label)}</td>
              <td className="align-right">
                {formatStoreOpenThousand(categoryAmount(code) ?? '0')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table>
        <tbody>
          <tr>
            <th colSpan={9}>{t('상세내역')}</th>
          </tr>
          <tr>
            <th>{t('대구분')}</th>
            <th>{t('중구분')}</th>
            <th>{t('소구분')}</th>
            <th>{t('내용', { context: 'item' })}</th>
            <th>{t('스펙')}</th>
            <th>{t('단위')}</th>
            <th>{t('백화점 기준단가')}</th>
            <th>{t('평/수량')}</th>
            <th>{t('예상 견적가')}</th>
          </tr>
          {lines.map((line, i) => {
            const span1 = spanAt(lines, i, cat1);
            const span2 = spanAt(lines, i, cat2);
            const span3 = spanAt(lines, i, cat3);
            const isGroupEnd =
              i === lines.length - 1 || cat1(lines[i + 1]) !== cat1(line);
            const groupSum = isGroupEnd
              ? lines
                  .filter((l) => cat1(l) === cat1(line))
                  .reduce((sum, l) => sum + thousand(l.amount), 0)
              : 0;
            return [
              <tr key={`line-${i}`}>
                {span1 > 0 && <td rowSpan={span1}>{line.category1Name}</td>}
                {span2 > 0 && <td rowSpan={span2}>{line.category2Name}</td>}
                {span3 > 0 && <td rowSpan={span3}>{line.category3Name}</td>}
                <td>{line.itemName}</td>
                <td>{line.spec}</td>
                <td>{line.unit}</td>
                <td className="align-right">
                  {formatStoreOpenThousand(line.unitPrice)}
                </td>
                <td>{line.quantity}</td>
                <td className="align-right">
                  {formatStoreOpenThousand(line.amount)}
                </td>
              </tr>,
              isGroupEnd && (
                <tr key={`sum-${i}`} className="store-open-subtotal">
                  <td colSpan={8}>{t('소계')}</td>
                  <td className="align-right">
                    {Math.round(groupSum).toLocaleString('en-US')}
                  </td>
                </tr>
              ),
            ];
          })}
        </tbody>
      </table>
    </StoreOpenDetailRoot>
  );
};

type DialogProps = Omit<BodyProps, 't'> & {
  open: boolean;
  onClose: () => void;
};

/** 「상세보기」 인테리어 비용 상세 내역 — PC 모달, 모바일 바텀시트 */
const StoreOpenInteriorDetailDialog = ({
  open,
  onClose,
  ...bodyProps
}: DialogProps) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = <InteriorDetailBody {...bodyProps} t={t} />;
  const title = t('인테리어 비용 상세 내역');

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={title}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <StoreOpenSheetBody>{body}</StoreOpenSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal ref={modalRef} title={title} handleClose={onClose}>
      {body}
    </Modal>
  );
};

export default StoreOpenInteriorDetailDialog;
